import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Apple,
  Smartphone,
  Share2,
  Copy,
  Check,
  ArrowRight,
  Heart,
  Video,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { plyxApi } from "@/lib/plyxApi";

/**
 * /plyx/join — Shareable launch landing page.
 *
 * Single-purpose page designed to be sent on WhatsApp / Twitter / Instagram
 * so friends can either (a) sign up immediately or (b) join the waitlist
 * for the mobile app launch. The tagline "where moment becomes movement"
 * runs through the hero, the share copy, and the OG meta description.
 *
 * Visual intent — bold gradient hero, dark canvas, generous whitespace.
 * Mobile-first; collapses gracefully to single column.
 */
const SHARE_TAGLINE =
  "Plyx — where moment becomes movement.\nJoin the African creator wave.\n";
const SHARE_URL = "https://plyx.co.uk/plyx/join";

export default function PlyxJoin() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);
  const [position, setPosition] = useState(null);
  const [copied, setCopied] = useState(false);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await plyxApi.post("/plyx/waitlist", { email });
      setJoined(true);
      setPosition(data?.position || null);
      toast.success(
        data?.position
          ? `You\u2019re #${data.position} on the list.`
          : "You\u2019re on the list. We\u2019ll ping you at launch.",
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Couldn\u2019t save your email — but you can still sign up below.",
      );
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${SHARE_TAGLINE}${SHARE_URL}`);
      setCopied(true);
      toast.success("Link + tagline copied — paste it anywhere.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn\u2019t copy — long-press to copy the link manually.");
    }
  };

  const shareNative = async () => {
    if (!navigator.share) return copyLink();
    try {
      await navigator.share({
        title: "Plyx — where moment becomes movement",
        text: SHARE_TAGLINE,
        url: SHARE_URL,
      });
    } catch {
      /* user dismissed share sheet — no-op */
    }
  };

  return (
    <div className="min-h-[100dvh] bg-black text-white overflow-x-hidden" data-testid="plyx-join-page">
      {/* Hero */}
      <div className="relative">
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(70% 60% at 50% 0%, rgba(255,45,122,0.6), transparent 70%), radial-gradient(50% 60% at 95% 30%, rgba(0,224,199,0.45), transparent 70%), radial-gradient(50% 50% at 10% 70%, rgba(255,180,45,0.4), transparent 70%)",
          }}
        />
        <div className="relative max-w-2xl mx-auto px-6 pt-16 pb-12 sm:pt-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-semibold tracking-wide text-white/85">
            <Sparkles className="w-3.5 h-3.5 text-[#FFB42D]" />
            COMING SOON · SIGN UP NOW
          </div>

          <h1
            className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight"
            style={{
              backgroundImage:
                "#E85D4E",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
            data-testid="plyx-join-headline"
          >
            Where
            <br />
            moment becomes
            <br />
            movement.
          </h1>

          <p className="text-lg sm:text-xl text-white/80 mt-7 max-w-lg leading-relaxed">
            The TikTok-style social network built for the African diaspora.
            Short videos, live broadcasts, real news, real creators —
            from London to Lagos to LA.
          </p>

          {/* Primary CTA */}
          <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:items-center">
            <Link
              to="/plyx/register"
              data-testid="plyx-join-signup-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-semibold text-black text-base shadow-lg shadow-[#FF2D7A]/30 hover:brightness-110 transition"
              style={{
                background:
                  "#E85D4E",
              }}
            >
              Sign up free
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/plyx/feed"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-semibold text-white text-base bg-white/5 hover:bg-white/10 border border-white/15 transition"
              data-testid="plyx-join-preview-btn"
            >
              Peek the feed
            </Link>
          </div>

          {/* Already have an account? */}
          <div className="mt-4 text-sm text-white/55">
            Already have an account?{" "}
            <Link to="/plyx/login" className="text-white underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>

      {/* Why Plyx — 3 reasons */}
      <div className="relative max-w-3xl mx-auto px-6 py-10 grid sm:grid-cols-3 gap-6">
        {[
          {
            icon: Video,
            title: "Real videos, no fluff",
            body: "BBC, Sky Sports, Channels TV, Arise News, AIT — plus creators like you.",
          },
          {
            icon: Heart,
            title: "Earn while you scroll",
            body: "Watch-to-earn coins, gift creators, build your following from day one.",
          },
          {
            icon: MessageCircle,
            title: "DMs + live streams",
            body: "Slide into DMs, go live for your tribe, host talent shows with real prizes.",
          },
        ].map(({ icon: Icon, title, body }) => (
          <div key={title} className="p-5 rounded-2xl bg-white/5 border border-white/10">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
              style={{
                background:
                  "#E85D4E",
              }}
            >
              <Icon className="w-5 h-5 text-black" />
            </div>
            <div className="font-semibold mb-1">{title}</div>
            <div className="text-sm text-white/65 leading-relaxed">{body}</div>
          </div>
        ))}
      </div>

      {/* Waitlist for App Store / Play Store launch */}
      <div className="relative max-w-2xl mx-auto px-6 py-10">
        <div className="rounded-3xl p-7 sm:p-9 bg-white/5 border border-white/10">
          <div className="flex flex-wrap items-center gap-3 text-xs text-white/65 mb-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/10">
              <Apple className="w-3.5 h-3.5" /> App Store — soon
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/10">
              <Smartphone className="w-3.5 h-3.5" /> Google Play — soon
            </span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl mb-2">
            Be first when the app drops.
          </h2>
          <p className="text-sm text-white/65 mb-5">
            Drop your email — we&rsquo;ll ping you the day it lands on
            App Store and Google Play.
          </p>
          {joined ? (
            <div
              className="px-4 py-3 rounded-xl bg-white/10/15 border border-[#00E0C7]/30 text-white/85 text-sm inline-flex items-center gap-2"
              data-testid="plyx-join-waitlist-success"
            >
              <Check className="w-4 h-4" />
              You&rsquo;re on the list{position ? ` — #${position}` : ""}.
            </div>
          ) : (
            <form onSubmit={submitWaitlist} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="plyx-join-waitlist-email"
                className="flex-1 px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF2D7A] transition"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="plyx-join-waitlist-submit"
                className="px-5 py-3 rounded-xl font-semibold text-black text-sm whitespace-nowrap disabled:opacity-50"
                style={{
                  background:
                    "#E85D4E",
                }}
              >
                {busy ? "Adding…" : "Notify me"}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Share row */}
      <div className="relative max-w-2xl mx-auto px-6 pb-20">
        <div className="text-xs uppercase tracking-wider text-white/45 mb-3">
          Help us launch
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={shareNative}
            data-testid="plyx-join-share-native"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-medium transition"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button
            type="button"
            onClick={copyLink}
            data-testid="plyx-join-share-copy"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-medium transition"
          >
            {copied ? <Check className="w-4 h-4 text-white/85"/> : <Copy className="w-4 h-4"/>}
            {copied ? "Copied" : "Copy link + tagline"}
          </button>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`${SHARE_TAGLINE}${SHARE_URL}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="plyx-join-share-whatsapp"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/30 text-sm font-medium text-[#25D366] transition"
          >
            WhatsApp
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TAGLINE)}&url=${encodeURIComponent(SHARE_URL)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="plyx-join-share-x"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-medium transition"
          >
            Post on X
          </a>
        </div>

        <div className="mt-10 text-[11px] text-white/35 leading-relaxed">
          Plyx Ltd · London · Free to join, free to watch, creators
          earn from day one.{" "}
          <Link to="/terms" className="underline">Terms</Link>
          {" · "}
          <Link to="/privacy" className="underline">Privacy</Link>
        </div>
      </div>
    </div>
  );
}
